import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Palette, Video, Wand2, ArrowRight } from "lucide-react";

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui";

const steps = [
  {
    title: "Upload the masterpiece",
    description:
      "Snap a photo of the drawing and drop it into our studio. PNG, JPG, and HEIC files are welcome.",
    icon: <Palette className="h-7 w-7 text-orange-500" />,
    badge: "Step 1",
  },
  {
    title: "Pick a magical theme",
    description:
      "Choose from fairy tales, ocean adventures, space races, and more—tailored for kids' imaginations.",
    icon: <Sparkles className="h-7 w-7 text-blue-500" />,
    badge: "Step 2",
  },
  {
    title: "Watch the story unfold",
    description:
      "Our AI animates the drawing into a short film with music and narration. Download and share instantly.",
    icon: <Video className="h-7 w-7 text-violet-500" />,
    badge: "Step 3",
  },
];

const testimonials = [
  {
    quote:
      "My daughter lit up seeing her crayon dragon take flight. The whole family keeps rewatching it!",
    author: "Linh, mom of Mia (age 6)",
  },
  {
    quote:
      "We used it for story time at preschool. The kids couldn’t stop talking about their animated characters.",
    author: "Jordan, early childhood educator",
  },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-soft text-foreground">
      <header className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 blur-3xl opacity-80"
          style={{
            background:
              "radial-gradient(circle at top left, rgba(251, 191, 36, 0.42), transparent 55%), radial-gradient(circle at bottom right, rgba(255, 232, 214, 0.32), transparent 60%)",
          }}
        />
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 py-8 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/"
            className="flex items-center gap-4 text-lg font-semibold text-foreground"
          >
            <span className="flex h-14 w-14 overflow-hidden rounded-3xl border-2 border-primary/40 bg-white shadow-xl shadow-brand/40">
              <img
                src="/media/logo.png"
                alt="DoodleMagic Studio logo"
                className="h-full w-full object-cover"
              />
            </span>
            <span className="font-heading text-xl">DoodleMagic Studio</span>
          </Link>
          <nav className="hidden items-center gap-6 text-sm font-semibold text-muted-foreground sm:flex" />
          <div className="hidden items-center gap-4 sm:flex">
            <Button
              asChild
              className="rounded-full px-6 py-2 text-sm font-semibold shadow-brand transition hover:-translate-y-0.5"
            >
              <Link to="/login" state={{ tab: "signup" }}>
                Launch studio
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-6xl gap-12 px-6 pb-24 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="flex flex-col gap-8">
            <span className="w-fit rounded-full bg-primary/10 px-4 py-1 text-sm font-semibold text-primary shadow-sm">
              Transform doodles into animated memories
            </span>
            <h1 className="text-4xl font-heading font-extrabold leading-tight md:text-5xl">
              Watch your child&apos;s imagination dance across the screen.
            </h1>
            <p className="max-w-xl text-lg text-muted-foreground">
              Upload a doodle and our story studio turns it into a music-filled mini film—sparkly, shareable, and ready in minutes.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button
                asChild
                className="rounded-full px-6 py-3 text-base font-semibold shadow-brand transition hover:-translate-y-0.5"
              >
                <Link to="/login" state={{ tab: "signup" }}>
                  Start the magic
                  <Sparkles className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                variant="outline"
                asChild
                className="rounded-full border-border/70 px-6 py-3 text-base font-semibold"
              >
                <a href="#how-it-works">See how it works</a>
              </Button>
            </div>
          </div>

          <Card className="relative overflow-hidden border-transparent bg-white/70 backdrop-blur">
            <div className="absolute -left-20 top-10 h-60 w-60 rounded-full bg-gradient-brand opacity-20 blur-3xl" />
            <CardHeader className="border-b border-border/40 pb-6">
              <CardTitle className="text-3xl font-heading font-semibold">
                Peek inside the DoodleMagic studio
              </CardTitle>
              <CardDescription className="text-base leading-relaxed">
                Follow a sample project as we animate a child&apos;s drawing with music, motion, and sparkle.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-black/5 shadow-inner">
                <video
                  className="h-full w-full object-cover"
                  src="/media/studio_preview.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-semibold text-white">
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Real studio preview
                  </span>
                  <span className="rounded-full bg-white/20 px-3 py-1 text-[10px] uppercase tracking-widest">
                    Sample animation
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Every adjustment you make—theme, pacing, colors—refreshes the preview instantly, so kids can
                watch their ideas take shape in real time.
              </p>
              <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-2xl bg-primary/10 px-4 py-3 font-semibold text-primary">
                  <Wand2 className="h-4 w-4" />
                  Guided creative themes
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-secondary/10 px-4 py-3 font-semibold text-secondary-foreground">
                  <Video className="h-4 w-4" />
                  Downloadable HD exports
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-muted/40 px-4 py-3 font-semibold text-muted-foreground sm:col-span-2">
                  <Sparkles className="h-4 w-4" />
                  Share instantly with friends and family
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </header>

      <main className="space-y-24 pb-24">
        <section id="how-it-works" className="mx-auto w-full max-w-6xl px-6">
          <div className="text-center">
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
              Simple and fun
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold">
              Three playful steps to bring doodles to life
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Designed for parents, educators, and kids to explore together—no design experience needed.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((step) => (
              <Card
                key={step.title}
                className="border border-border/40 bg-white/80 p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    {step.badge}
                  </span>
                  <div className="rounded-full bg-background p-2 shadow-sm">{step.icon}</div>
                </div>
                <h3 className="mt-7 font-heading text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </Card>
            ))}
          </div>
        </section>

        <section id="stories" className="border-y border-border/40 bg-white/70 py-20">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 lg:flex-row">
            <div className="max-w-xl">
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-widest text-secondary-foreground">
                Loved by families
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold">
                Magical moments shared every night
              </h2>
              <p className="mt-3 text-base text-muted-foreground">
                From bedtime premiers to classroom story time, DoodleMagic helps kids see
                their creativity celebrated in motion.
              </p>
            </div>
            <div className="grid flex-1 gap-6 md:grid-cols-2">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.author} className="border border-border/40 bg-muted/20 p-6">
                  <CardContent className="space-y-4 p-0">
                    <p className="text-base leading-relaxed text-foreground">
                      “{testimonial.quote}”
                    </p>
                    <span className="text-sm font-semibold text-muted-foreground">
                      {testimonial.author}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto flex w-full max-w-5xl flex-col items-center gap-6 px-6 text-center">
          <h2 className="font-heading text-3xl font-semibold md:text-4xl">
            Ready to turn the next doodle into an epic?
          </h2>
          <p className="max-w-2xl text-lg text-muted-foreground">
            Join thousands of parents and educators surprising their kids with cinematic
            stories every week. Your first storybook video is on us.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Button
              asChild
              className="rounded-full px-6 py-3 text-base font-semibold shadow-brand"
            >
              <Link to="/login">
                Log in to animate
                <Wand2 className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              variant="outline"
              asChild
              className="rounded-full border-border/70 px-6 py-3 text-base font-semibold"
            >
              <Link to="/login" state={{ tab: "signup" }}>
                Create a free account
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 bg-background/80 py-10 text-sm text-muted-foreground">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 px-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-center gap-3 font-semibold text-foreground">
            <span className="flex h-12 w-12 overflow-hidden rounded-3xl border border-primary/20 bg-white shadow-lg shadow-brand/30">
              <img
                src="/media/logo.png"
                alt="DoodleMagic Studio logo"
                className="h-full w-full object-cover"
              />
            </span>
            DoodleMagic Studio
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-4 text-sm font-semibold text-muted-foreground">
            <Link to="/support" className="hover:text-foreground">
              Support
            </Link>
            <Link to="/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              Terms of Service
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} DoodleMagic. Crafted with delight.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;